import type {
  CurrentInventoryDetailRow,
  CurrentInventoryStatusFilter,
  CurrentInventorySummaryRow,
} from '../types';

export type StatusOption = {
  label: string;
  value: 'all' | CurrentInventoryStatusFilter;
};

export type SubgroupOption = {
  label: string;
  value: 'all' | string;
};

export type GameFilterOption = {
  label: string;
  value: 'all' | string;
};

export type InventoryTotal = {
  packCount: number;
  tickets: number;
  amount: number;
};

export type CountBreakdown = {
  label: string;
  count: number;
  colorClass?: string;
};

export const statusOptions: StatusOption[] = [
  { label: 'All', value: 'all' },
  { label: 'In Transit', value: 'In Transit' },
  { label: 'Received', value: 'Received' },
  { label: 'Activated', value: 'Activated' },
  { label: 'Settled', value: 'Settled' },
  { label: 'Returned', value: 'Returned' },
];

export const emptyInventoryTotal: InventoryTotal = {
  packCount: 0,
  tickets: 0,
  amount: 0,
};

export const countKey = (value: string | null | undefined) => (value ?? '').toLowerCase();

export const getStatusColorClass = (value: string) =>
  `current-inventory-status-color--${countKey(value || 'all').replace(/\s+/g, '-')}`;

const subgroupSortRank = (value: string | null | undefined) => {
  const key = countKey(value);
  if (key === 'backstock') {
    return 900;
  }
  if (key === 'sold out') {
    return 901;
  }
  if (key === 'returned') {
    return 902;
  }
  return 100;
};

const compareSubgroupValues = (left: string, right: string) => {
  const rankDelta = subgroupSortRank(left) - subgroupSortRank(right);
  if (rankDelta !== 0) {
    return rankDelta;
  }
  return left.localeCompare(right);
};

const isStatusFilterValue = (value: unknown): value is CurrentInventoryStatusFilter =>
  typeof value === 'string'
  && statusOptions.some((option) => option.value === value && option.value !== 'all');

export const getSelectedStatuses = (status: unknown): Array<'all' | CurrentInventoryStatusFilter> => {
  const values = (Array.isArray(status) ? status : [status])
    .flatMap((value) => typeof value === 'string' ? value.split(',') : [value])
    .map((value) => typeof value === 'string' ? value.trim() : value)
    .filter((value): value is 'all' | CurrentInventoryStatusFilter =>
      value === 'all' || isStatusFilterValue(value)
    );

  if (values.includes('all')) {
    return ['all'];
  }

  return values.length > 0 ? values : ['all'];
};

export const isAllStatusSelected = (statuses: Array<'all' | CurrentInventoryStatusFilter>) =>
  statuses.includes('all');

export const filterRowsByStatuses = <TRow extends { status: string | null }>(
  rows: TRow[],
  statuses: Array<'all' | CurrentInventoryStatusFilter>
) => {
  if (isAllStatusSelected(statuses)) {
    return rows;
  }

  const selected = new Set(statuses);
  return rows.filter((row) =>
    row.status !== null && selected.has(row.status as CurrentInventoryStatusFilter)
  );
};

export const getSelectedGameId = (value: unknown) => {
  const selected = typeof value === 'string' ? value.trim() : '';
  return selected || 'all';
};

export const filterRowsByGame = <TRow extends { game_id: string }>(
  rows: TRow[],
  gameId: string
) => {
  if (gameId === 'all') {
    return rows;
  }

  return rows.filter((row) => row.game_id === gameId);
};

const normalizeInventorySearchText = (value: unknown) =>
  String(value ?? '').trim().toLowerCase();

const normalizeInventorySearchDigits = (value: unknown) =>
  normalizeInventorySearchText(value).replace(/\D/g, '');

const addInventorySearchValue = (values: Set<string>, value: unknown) => {
  const text = normalizeInventorySearchText(value);
  if (!text) {
    return;
  }

  values.add(text);

  const compactText = text.replace(/[\s-]+/g, '');
  if (compactText) {
    values.add(compactText);
  }

  const digits = normalizeInventorySearchDigits(text);
  if (digits) {
    values.add(digits);
  }
};

const buildInventorySearchTerms = (search: string) => {
  const normalizedSearch = normalizeInventorySearchText(search);
  if (!normalizedSearch) {
    return [];
  }

  const terms = new Set<string>();
  normalizedSearch
    .split(/[\s-]+/)
    .map((term) => term.trim())
    .filter(Boolean)
    .forEach((term) => {
      terms.add(term);

      const digits = normalizeInventorySearchDigits(term);
      if (digits) {
        terms.add(digits);
      }
    });

  return Array.from(terms);
};

const buildInventorySearchValues = (row: CurrentInventoryDetailRow) => {
  const values = new Set<string>();
  const packIds = [
    row.pack_id,
    row.display_pack_id,
    row.raw_pack_id,
  ];

  addInventorySearchValue(values, row.game_id);
  addInventorySearchValue(values, row.game_name);
  packIds.forEach((packId) => addInventorySearchValue(values, packId));
  packIds.forEach((packId) => {
    addInventorySearchValue(values, `${row.game_id} ${packId}`);
    addInventorySearchValue(values, `${row.game_id}-${packId}`);
  });

  return Array.from(values);
};

export const filterRowsByInventorySearch = (
  rows: CurrentInventoryDetailRow[],
  search: string
) => {
  const terms = buildInventorySearchTerms(search);

  if (terms.length === 0) {
    return rows;
  }

  return rows.filter((row) => {
    const values = buildInventorySearchValues(row);
    return terms.every((term) =>
      values.some((value) => value.includes(term))
    );
  });
};

type CurrentInventoryDetailFilterOptions = {
  gameId: string;
  statuses: Array<'all' | CurrentInventoryStatusFilter>;
  search: string;
};

export const filterCurrentInventoryDetails = (
  rows: CurrentInventoryDetailRow[],
  {
    gameId,
    statuses,
    search,
  }: CurrentInventoryDetailFilterOptions
) => filterRowsByInventorySearch(
  filterRowsByStatuses(
    filterRowsByGame(rows, gameId),
    statuses
  ),
  search
);

export const buildGameFilterLabel = (gameId: string, gameName: string | null | undefined) => {
  const name = (gameName || '').trim();
  return name ? `${gameId} - ${name}` : gameId;
};

export const toNumericTotal = (value: number | string | null | undefined) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getInventoryRowKey = (row: CurrentInventoryDetailRow) => `${row.game_id}-${row.pack_id}`;

export const mergeInventoryDetails = (...groups: CurrentInventoryDetailRow[][]) => {
  const merged = new Map<string, CurrentInventoryDetailRow>();
  groups.flat().forEach((row) => {
    merged.set(getInventoryRowKey(row), row);
  });
  return Array.from(merged.values());
};

export const buildInventoryTotals = (
  rows: CurrentInventorySummaryRow[],
  field: 'status' | 'subgroup'
) => {
  const totals: Record<string, InventoryTotal> = {
    all: { ...emptyInventoryTotal },
  };

  rows.forEach((row) => {
    const key = countKey(row[field]);
    const current = totals[key] ?? { ...emptyInventoryTotal };

    totals.all.packCount += row.pack_count;
    totals.all.tickets += row.remaining_tickets;
    totals.all.amount += toNumericTotal(row.remaining_value);
    current.packCount += row.pack_count;
    current.tickets += row.remaining_tickets;
    current.amount += toNumericTotal(row.remaining_value);
    totals[key] = current;
  });

  return totals;
};

export const buildInventorySummaryFromDetails = (
  rows: CurrentInventoryDetailRow[]
): CurrentInventorySummaryRow[] => {
  const groups = new Map<string, CurrentInventorySummaryRow>();

  rows.forEach((row) => {
    const status = row.status ?? null;
    const subgroup = row.subgroup ?? null;
    const key = `${status ?? ''}::${subgroup ?? ''}`;
    const current = groups.get(key) ?? {
      status,
      subgroup,
      pack_count: 0,
      remaining_tickets: 0,
      remaining_value: 0,
    };

    current.pack_count += 1;
    current.remaining_tickets += toNumericTotal(row.remaining_tickets);
    current.remaining_value = toNumericTotal(current.remaining_value) + toNumericTotal(row.remaining_value);
    groups.set(key, current);
  });

  return Array.from(groups.values());
};

export const buildSubgroupOptions = (rows: CurrentInventorySummaryRow[]): SubgroupOption[] => {
  const subgroups = new Set<string>();

  rows.forEach((row) => {
    if (row.subgroup) {
      subgroups.add(row.subgroup);
    }
  });

  return [
    { label: 'All', value: 'all' },
    ...Array.from(subgroups)
      .sort(compareSubgroupValues)
      .map((subgroup) => ({ label: subgroup, value: subgroup })),
  ];
};

export const filterOutSoldOutAndReturnedRows = <TRow extends {
  status: string | null;
  subgroup: string | null;
}>(
  rows: TRow[],
  shouldIncludeSoldOut: boolean
) => {
  if (shouldIncludeSoldOut) {
    return rows;
  }

  return rows.filter((row) =>
    row.subgroup !== 'Sold Out' && row.subgroup !== 'Returned'
  );
};

export const buildSubgroupStatusBreakdowns = (rows: CurrentInventorySummaryRow[]) => {
  const breakdowns: Record<string, Record<string, number>> = {
    all: {},
  };

  rows.forEach((row) => {
    const subgroupKey = countKey(row.subgroup);
    const statusKey = countKey(row.status);

    breakdowns.all[statusKey] = (breakdowns.all[statusKey] ?? 0) + row.pack_count;

    const subgroupBreakdown = breakdowns[subgroupKey] ?? {};
    subgroupBreakdown[statusKey] = (subgroupBreakdown[statusKey] ?? 0) + row.pack_count;
    breakdowns[subgroupKey] = subgroupBreakdown;
  });

  return Object.entries(breakdowns).reduce<Record<string, CountBreakdown[]>>(
    (result, [subgroupKey, statusCounts]) => {
      result[subgroupKey] = statusOptions
        .filter((option) => option.value !== 'all')
        .map((option) => ({
          label: option.label,
          count: statusCounts[countKey(option.value)] ?? 0,
          colorClass: getStatusColorClass(option.value),
        }))
        .filter((item) => item.count > 0);

      return result;
    },
    {}
  );
};

export const buildStatusSubgroupBreakdowns = (rows: CurrentInventorySummaryRow[]) => {
  const breakdowns: Record<string, Record<string, number>> = {
    all: {},
  };

  rows.forEach((row) => {
    const statusKey = countKey(row.status);
    const subgroupKey = countKey(row.subgroup);

    breakdowns.all[subgroupKey] = (breakdowns.all[subgroupKey] ?? 0) + row.pack_count;

    const statusBreakdown = breakdowns[statusKey] ?? {};
    statusBreakdown[subgroupKey] = (statusBreakdown[subgroupKey] ?? 0) + row.pack_count;
    breakdowns[statusKey] = statusBreakdown;
  });

  return Object.entries(breakdowns).reduce<Record<string, CountBreakdown[]>>(
    (result, [statusKey, subgroupCounts]) => {
      result[statusKey] = buildSubgroupOptions(rows)
        .filter((option) => option.value !== 'all')
        .map((option) => ({
          label: option.label,
          count: subgroupCounts[countKey(option.value)] ?? 0,
          colorClass: getStatusColorClass(statusKey),
        }))
        .filter((item) => item.count > 0);

      return result;
    },
    {}
  );
};

export const getTotalForOption = (
  totals: Record<string, InventoryTotal>,
  value: string
) => totals[value === 'all' ? 'all' : countKey(value)] ?? emptyInventoryTotal;

export const getBreakdownForOption = (
  breakdowns: Record<string, CountBreakdown[]>,
  value: string
) => breakdowns[value === 'all' ? 'all' : countKey(value)] ?? [];

export const buildStatusGuidance = (
  status: StatusOption['value'],
  includeSoldOut: boolean
) => {
  switch (status) {
    case 'In Transit':
      return 'and not received';
    case 'Received':
      return 'available to Activate';
    case 'Activated':
      return includeSoldOut ? '' : 'and not sold out';
    case 'Settled':
      return includeSoldOut ? '' : 'but not sold out';
    default:
      return '';
  }
};
